import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown, CaretRight, Minus, ArrowsOutSimple, Flag, SpeakerHigh, Stop, CircleNotch } from "@phosphor-icons/react";
import { MOVES, fmtClock, fmtLatency, moveLabel } from "@/lib/format";

const SpeakButton = ({ a, speaking, onSpeak, live }) => {
  const title = speaking === "playing" ? "Stop reading" : speaking === "loading" ? "Preparing audio… (click to cancel)" : `Read aloud${live ? " · the mic pauses while it speaks" : ""}`;
  return (
    <button onClick={() => onSpeak(a.n)} className={`p-1 -m-1 transition-colors ${speaking ? "text-[var(--o-fg)]" : "text-[var(--o-muted2)] hover:text-[var(--o-fg)]"}`} title={title} aria-label={title} data-testid={`card-speak-${a.n}`}>
      {speaking === "loading" ? <CircleNotch size={13} className="animate-spin" /> : speaking === "playing" ? <Stop size={13} weight="fill" /> : <SpeakerHigh size={13} />}
    </button>
  );
};

const Header = ({ a, onToggle, dismissed, canSpeak, speaking, onSpeak, live }) => (
  <div className="flex items-center gap-2.5 text-xs">
    <span className="font-mono text-[var(--o-fg)] font-medium" data-testid="card-number">#{String(a.n).padStart(2, "0")}</span>
    {a.status === "done" && a.shown && (
      <span className="font-mono uppercase tracking-[0.1em] text-[10px] px-1.5 py-0.5 border" style={{ color: MOVES[a.move]?.color, borderColor: `${MOVES[a.move]?.color}55` }} data-testid="card-move">
        {moveLabel(a.move)}
      </span>
    )}
    {a.source === "flag" && <Flag size={12} weight="fill" className="text-[#FF3B30]" />}
    <span className="font-mono text-[var(--o-muted3)] ml-auto" data-testid="card-meta">
      {a.provider}{a.reasoning_effort && a.reasoning_effort !== "default" ? ` · ${a.reasoning_effort}` : ""} · {fmtLatency(a.latency_ms)}
    </span>
    {a.status === "done" && a.shown && canSpeak && <SpeakButton a={a} speaking={speaking} onSpeak={onSpeak} live={live} />}
    {a.status === "done" && a.shown && (
      <button onClick={onToggle} className="text-[var(--o-muted2)] hover:text-[var(--o-fg)] p-1 -m-1 transition-colors" title={dismissed ? "Reopen" : "Dismiss"} data-testid={`card-dismiss-toggle-${a.n}`}>
        {dismissed ? <ArrowsOutSimple size={13} /> : <Minus size={13} weight="bold" />}
      </button>
    )}
  </div>
);

const Grounds = ({ grounds, n }) => {
  const [open, setOpen] = useState(false);
  if (!grounds?.length) return null;
  return (
    <div className="border-t border-[var(--o-border)] pt-3">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--o-muted2)] hover:text-[var(--o-fg)] transition-colors" data-testid={`card-grounds-toggle-${n}`}>
        {open ? <CaretDown size={11} /> : <CaretRight size={11} />} Grounds · {grounds.length}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ul initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden space-y-1.5 mt-2" data-testid={`card-grounds-${n}`}>
            {grounds.map((g, i) => (
              <li key={i} className="font-mono text-[11px] leading-relaxed text-[var(--o-muted)] pl-3 border-l border-[var(--o-border-strong)]">{g}</li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ObserverCard = ({ a, onDismiss, canSpeak = false, speaking = null, onSpeak, live = false }) => {
  if (a.status === "pending") {
    return (
      <div className="border border-[var(--o-border)] p-4 space-y-3" data-testid={`observer-card-${a.n}`}>
        <div className="flex items-center gap-2 text-xs font-mono"><span>#{String(a.n).padStart(2, "0")}</span><span className="text-[var(--o-muted3)]">thinking on {fmtClock(a.transcript_seconds)} of transcript…</span></div>
        <div className="h-3 w-4/5 shimmer" /><div className="h-3 w-3/5 shimmer" />
      </div>
    );
  }
  if (a.status === "error") {
    return (
      <div className="border border-[#EF4444]/40 px-4 py-3 text-xs" data-testid={`observer-card-${a.n}`}>
        <span className="font-mono">#{String(a.n).padStart(2, "0")}</span> <span className="text-[#EF4444]">Observer call failed</span>
        <p className="font-mono text-[11px] text-[var(--o-muted2)] mt-1 break-words">{a.error}</p>
      </div>
    );
  }
  if (!a.shown) {
    return (
      <div className="flex items-center gap-3 border border-[var(--o-border-soft)] px-4 py-2.5 text-xs text-[var(--o-muted3)]" title={a.missing_data ? `${a.question}\n${a.missing_data}` : a.question} data-testid={`observer-card-${a.n}`}>
        <span className="font-mono">#{String(a.n).padStart(2, "0")}</span>
        <span className="min-w-0 truncate" data-testid="card-nothing-to-add">{a.missing_data ? `Nothing grounded to add: ${a.missing_data}` : "Nothing to add yet"}</span>
        <span className="font-mono ml-auto">{fmtLatency(a.latency_ms)}</span>
      </div>
    );
  }
  const dismissed = a.dismissed;
  return (
    <motion.div layout className={`border ${dismissed ? "border-[var(--o-border-soft)]" : "border-[var(--o-border)] bg-[var(--o-card)]"} p-4`} data-testid={`observer-card-${a.n}`}>
      <Header a={a} dismissed={dismissed} onToggle={() => onDismiss(a.n, !dismissed)} canSpeak={canSpeak} speaking={speaking} onSpeak={onSpeak} live={live} />
      <AnimatePresence initial={false}>
        {dismissed ? (
          <motion.p key="line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-[var(--o-muted3)] truncate mt-2" data-testid={`card-collapsed-${a.n}`}>{a.text}</motion.p>
        ) : (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="font-mono text-[11px] text-[var(--o-muted3)] mt-3" data-testid="card-question">{a.source === "open" ? "Open read" : a.source === "flag" ? "From flag" : "Q"} · {a.question}</p>
            <p className="text-[15px] leading-relaxed text-[var(--o-fg)] mt-2 mb-4" data-testid="card-text">{a.text}</p>
            <Grounds grounds={a.grounds} n={a.n} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
